import {
    EpisodeDocumentResponse,
    EpisodeVersionDocumentResponse,
    TvSeasonDocumentResponse,
    TvSeriesDocumentResponse
} from "../../../common/api/catalogue-api/tv-api/TvSeriesDocumentResponse";
import { ContentSource } from "../../../common/types/ContentSource";

export interface TvSeriesListingItem {
    id: string;
    title: string;
    buzzId?: string;
    orphaned: boolean;
    includedSources: ContentSource[];
    isModified?: boolean;
    merged?: boolean;
    orphanedDetails: string[];
}

export const mapToTvSeriesLisingItem = (item: TvSeriesDocumentResponse, isModified?: boolean): TvSeriesListingItem => {
    return {
        id: item.id,
        title: item.title,
        buzzId: item.buzzId,
        orphaned: item.orphaned,
        includedSources: item.includedSources,
        merged: !!item.mergedTargetId,
        orphanedDetails: getTvSeriesOrphanedDetails(item),
        isModified
    };
};

const getTvSeriesOrphanedDetails = (tvSeriesResponse?: TvSeriesDocumentResponse): string[] => {
    if (!tvSeriesResponse) {
        return ["TvSeries is orphaned"];
    }

    const messages: string[] = [];
    const tvSeriesOrphanedInfo = getTvSeriesOrphanedInfo(tvSeriesResponse);

    if (tvSeriesOrphanedInfo.orphaned) {
        messages.push("TvSeries is orphaned");
    }

    if (tvSeriesOrphanedInfo.orphanedSeasonsCount > 0) {
        messages.push(`Orphaned seasons count: ${tvSeriesOrphanedInfo.orphanedSeasonsCount}`);
    }

    if (tvSeriesOrphanedInfo.orphanedEpisodesCount > 0) {
        messages.push(`Orphaned episodes count: ${tvSeriesOrphanedInfo.orphanedEpisodesCount}`);
    }

    if (tvSeriesOrphanedInfo.orphanedEpsiodeVersionsCount > 0) {
        messages.push(`Orphaned versions count: ${tvSeriesOrphanedInfo.orphanedEpsiodeVersionsCount}`);
    }

    return messages;
};

const getTvSeriesOrphanedInfo = (tvSeriesResponse: TvSeriesDocumentResponse) => {
    const seasonsOrphanedInfo = getSeasonsOrphanedInfo(tvSeriesResponse.seasons);
    const orphaned = !tvSeriesResponse.buzzId || seasonsOrphanedInfo.orphanedSeasonsCount > 0;

    return {
        ...seasonsOrphanedInfo,
        orphaned
    };
};

const getSeasonsOrphanedInfo = (seasons: TvSeasonDocumentResponse[]) => {
    let orphanedEpsiodeVersionsCount = 0;
    let orphanedEpisodesCount = 0;
    const orphanedSeasons: TvSeasonDocumentResponse[] = [];

    seasons.forEach((season: TvSeasonDocumentResponse) => {
        const episodesOrphanedInfo = getEpisodesOrphanedInfo(season.episodes);
        orphanedEpsiodeVersionsCount += episodesOrphanedInfo.orphanedEpsiodeVersionsCount;
        orphanedEpisodesCount += episodesOrphanedInfo.orphanedEpisodesCount;

        if (seasonOrphaned(season, episodesOrphanedInfo.orphanedEpisodesCount)) {
            orphanedSeasons.push(season);
        }
    });

    const orphanedSeasonsCount = orphanedSeasons.length;

    return { orphanedEpsiodeVersionsCount, orphanedEpisodesCount, orphanedSeasonsCount };
};

const seasonOrphaned = (season: TvSeasonDocumentResponse, orphanedEpisodesCount: number) => {
    return (!season.buzzId || orphanedEpisodesCount > 0) && !season.mergedTargetId;
};

const getEpisodesOrphanedInfo = (episodes: EpisodeDocumentResponse[]) => {
    let orphanedEpsiodeVersionsCount = 0;
    const orphanedEpisodes: EpisodeDocumentResponse[] = [];

    episodes.forEach((episode: EpisodeDocumentResponse) => {
        const orphanedVersionsCount = getEpisodeVersionsOrphanedCount(episode.versions);
        orphanedEpsiodeVersionsCount += orphanedVersionsCount;

        if (episodeOrphaned(episode, orphanedVersionsCount)) {
            orphanedEpisodes.push(episode);
        }
    });

    const orphanedEpisodesCount = orphanedEpisodes.length;

    return { orphanedEpsiodeVersionsCount, orphanedEpisodesCount };
};

const episodeOrphaned = (episode: EpisodeDocumentResponse, orphanedVersionsCount: number) => {
    return (!episode.buzz?.id || orphanedVersionsCount > 0) && !episode.mergedTargetId;
};

const getEpisodeVersionsOrphanedCount = (episodeVersions: EpisodeVersionDocumentResponse[]): number => {
    const orphanedVersions = episodeVersions.filter(
        (version: EpisodeVersionDocumentResponse) => !version.buzzVersion && !version.mergedTargetId
    );

    return orphanedVersions.length;
};
